// projectListDerivation.js
//
// Pure, side-effect-free helpers that derive the Project_List view model from
// the managed-project payloads returned by the Admin_Project_Api. Every helper
// is total (never throws, never mutates its input) so a single malformed record
// can never break the admin list render, mirroring the conventions used by
// `projectNormalizer.js` and `serviceCategories.js`.
//
// These helpers are consumed by ProjectList.vue (task 12.1) and are covered by
// property tests (tasks 11.2–11.6). They cover:
//   - live/hidden/proposal state derivation      (Requirements 2.3, 2.4, 2.5)
//   - row view-model derivation with placeholders (Requirement 2.2)
//   - case-insensitive search + category + state filtering
//                                                 (Requirements 3.1, 3.2, 3.3, 3.6)
//   - sortable-column ordering                    (Requirement 3.4)
//   - 25-row pagination chunking                  (Requirement 2.9)
//
// ManagedProject (raw input shape from GET /api/administration/projects):
//   {
//     id, job_id, title, description, project_type_id,
//     serviceCategory,   // ProjectTypes.type
//     client_id, clientName,
//     show_on_website, proposal, display_order,
//     imageUrl, imageAlt
//   }

/**
 * Display placeholder shown in place of any absent row value (Requirement 2.2).
 * @type {string}
 */
export const PLACEHOLDER = '—'

/**
 * Derived live/hidden/proposal display states (Requirements 2.3, 2.4, 2.5).
 */
export const STATE_LIVE = 'live'
export const STATE_HIDDEN = 'hidden'
export const STATE_PROPOSAL = 'proposal'

/**
 * Sentinel selection meaning "do not filter" for the category and state
 * filters. When a filter is set to this value (or is empty/absent) it is
 * inactive (Requirement 3.6).
 * @type {string}
 */
export const ALL = 'all'

/**
 * Fixed page size for the Project_List pagination (Requirement 2.9).
 * @type {number}
 */
export const PAGE_SIZE = 25

/**
 * The sortable Project_List columns.
 * @type {ReadonlyArray<string>}
 */
export const SORTABLE_COLUMNS = Object.freeze([
  'title',
  'serviceCategory',
  'clientName',
  'state',
  'displayOrder'
])

/**
 * Coerce an arbitrary value into a trimmed, non-empty display string, or return
 * null when the value cannot be sensibly represented as displayable text.
 *
 * @param {*} value
 * @returns {string|null}
 */
function toDisplayString (value) {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' ? null : trimmed
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return String(value)
  // Objects, arrays, functions, NaN, Infinity, symbols, bigints -> not text.
  return null
}

/**
 * Derive a Project's live/hidden/proposal display state from the two boolean
 * flags (Requirements 2.3, 2.4, 2.5).
 *
 * - live     when show_on_website === true  AND proposal === false
 * - proposal when show_on_website === true  AND proposal === true
 * - hidden   when show_on_website === false (regardless of proposal)
 *
 * The flags are compared by strict equality with `true`, so any absent,
 * null, or non-boolean flag is treated as falsey. This keeps the function
 * total over arbitrary input.
 *
 * @param {*} showOnWebsite
 * @param {*} proposal
 * @returns {('live'|'hidden'|'proposal')}
 */
export function deriveState (showOnWebsite, proposal) {
  if (showOnWebsite !== true) return STATE_HIDDEN
  return proposal === true ? STATE_PROPOSAL : STATE_LIVE
}

/**
 * Derive a single Project_List row view model from a raw managed-project
 * record (Requirement 2.2, 2.3, 2.4, 2.5).
 *
 * Total: tolerates any input shape (including null/undefined or wrong types)
 * and always returns a complete row where every displayed column
 * (title, serviceCategory, clientName, displayOrder) holds either the real
 * value or {@link PLACEHOLDER} — never an empty or undefined display value.
 * `state` is always one of the three derived states.
 *
 * The `id`, `showOnWebsite`, and `proposal` fields are carried through for
 * keying, actions, and filtering; they are not display columns.
 *
 * @param {*} raw - a managed-project record from the Admin_Project_Api
 * @returns {{
 *   id: (string|number|null),
 *   title: string,
 *   serviceCategory: string,
 *   clientName: string,
 *   state: ('live'|'hidden'|'proposal'),
 *   displayOrder: (number|string),
 *   showOnWebsite: boolean,
 *   proposal: boolean
 * }}
 */
export function deriveRowViewModel (raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}

  const showOnWebsite = source.show_on_website === true
  const proposal = source.proposal === true

  const displayOrderValue =
    typeof source.display_order === 'number' && Number.isFinite(source.display_order)
      ? source.display_order
      : PLACEHOLDER

  // The multi-select set of category labels, when present. Falls back to an
  // empty array so `serviceCategories.includes(...)` is always safe to call.
  const serviceCategories = Array.isArray(source.serviceCategories)
    ? source.serviceCategories.filter((c) => typeof c === 'string' && c.trim() !== '')
    : []

  return {
    id: source.id !== undefined && source.id !== null ? source.id : null,
    title: toDisplayString(source.title) || PLACEHOLDER,
    serviceCategory: toDisplayString(source.serviceCategory) || PLACEHOLDER,
    serviceCategories,
    clientName: toDisplayString(source.clientName) || PLACEHOLDER,
    state: deriveState(source.show_on_website, source.proposal),
    displayOrder: displayOrderValue,
    showOnWebsite,
    proposal
  }
}

/**
 * Derive the full list of row view models from a raw managed-project list.
 * Tolerates a non-array input by returning an empty array.
 *
 * @param {*} rawList
 * @returns {Array} row view models
 */
export function deriveRows (rawList) {
  if (!Array.isArray(rawList)) return []
  return rawList.map(deriveRowViewModel)
}

/**
 * Return the text of a row column usable for a case-insensitive search match.
 * A placeholder value is not real text and therefore never matches a search.
 *
 * @param {string} value
 * @returns {string}
 */
function searchableText (value) {
  if (typeof value !== 'string' || value === PLACEHOLDER) return ''
  return value.toLowerCase()
}

/**
 * Filter a list of row view models by the active search / category / state
 * criteria (Requirements 3.1, 3.2, 3.3, 3.6).
 *
 * - `search`: case-insensitive substring match against the row's title OR
 *   client name. An empty/whitespace search is inactive.
 * - `category`: exact match against the row's `serviceCategory`. A value of
 *   {@link ALL} (or empty/absent) is inactive.
 * - `state`: exact match against the row's derived `state`. A value of
 *   {@link ALL} (or empty/absent) is inactive.
 *
 * Sound and complete: every returned row satisfies all active criteria and no
 * excluded row satisfies all active criteria. When no criteria are active the
 * result is a copy of the full input list. Pure and total: never mutates the
 * input and never throws.
 *
 * @param {Array} rows - row view models (from {@link deriveRows})
 * @param {{ search?: string, category?: string, state?: string }} [criteria]
 * @returns {Array} the filtered rows (a new array)
 */
export function filterRows (rows, criteria = {}) {
  if (!Array.isArray(rows)) return []

  const source = criteria && typeof criteria === 'object' ? criteria : {}

  const searchTerm =
    typeof source.search === 'string' ? source.search.trim().toLowerCase() : ''
  const categoryFilter =
    typeof source.category === 'string' && source.category !== ALL
      ? source.category
      : null
  const stateFilter =
    typeof source.state === 'string' && source.state !== ALL
      ? source.state
      : null

  return rows.filter(row => {
    if (!row || typeof row !== 'object') return false

    if (searchTerm !== '') {
      const matchesSearch =
        searchableText(row.title).includes(searchTerm) ||
        searchableText(row.clientName).includes(searchTerm)
      if (!matchesSearch) return false
    }

    if (categoryFilter !== null) {
      const matchesCategory = Array.isArray(row.serviceCategories) && row.serviceCategories.length > 0
        ? row.serviceCategories.includes(categoryFilter)
        : row.serviceCategory === categoryFilter
      if (!matchesCategory) return false
    }

    if (stateFilter !== null && row.state !== stateFilter) {
      return false
    }

    return true
  })
}

/**
 * Compare two row view models by the given column. Numbers are compared
 * numerically; every other value is compared as a case-insensitive string.
 * This yields a deterministic total order used by {@link sortRows}.
 *
 * @param {Object} a
 * @param {Object} b
 * @param {string} column - one of {@link SORTABLE_COLUMNS}
 * @returns {number} negative, 0, or positive
 */
export function compareByColumn (a, b, column) {
  const av = a ? a[column] : undefined
  const bv = b ? b[column] : undefined

  if (typeof av === 'number' && typeof bv === 'number') {
    return av - bv
  }

  const as = av === null || av === undefined ? '' : String(av).toLowerCase()
  const bs = bv === null || bv === undefined ? '' : String(bv).toLowerCase()

  if (as < bs) return -1
  if (as > bs) return 1
  return 0
}

/**
 * Sort a list of row view models by a sortable column in the given direction
 * (Requirement 3.4).
 *
 * Returns a new array that is a permutation of the input (same multiset of
 * rows) whose values along the chosen column are monotonic in the chosen
 * direction. Uses a stable sort so equal-keyed rows keep their input order.
 * When the column is not sortable, the input order is preserved.
 *
 * @param {Array} rows - row view models
 * @param {string} column - one of {@link SORTABLE_COLUMNS}
 * @param {('asc'|'desc')} [direction='asc']
 * @returns {Array} the sorted rows (a new array)
 */
export function sortRows (rows, column, direction = 'asc') {
  if (!Array.isArray(rows)) return []

  const copy = rows.slice()
  if (!SORTABLE_COLUMNS.includes(column)) return copy

  const sign = direction === 'desc' ? -1 : 1
  return copy.sort((a, b) => sign * compareByColumn(a, b, column))
}

/**
 * Chunk a list of rows into fixed-size pages of at most {@link PAGE_SIZE}
 * (25) rows each (Requirement 2.9).
 *
 * The in-order concatenation of all returned pages equals the input list, and
 * every page holds at most `pageSize` rows. An empty input yields an empty
 * array of pages. Pure and total: never mutates the input and never throws.
 *
 * @param {Array} rows - the (already filtered/sorted) rows to paginate
 * @param {number} [pageSize=PAGE_SIZE]
 * @returns {Array<Array>} the pages
 */
export function paginate (rows, pageSize = PAGE_SIZE) {
  if (!Array.isArray(rows)) return []

  const size =
    typeof pageSize === 'number' && Number.isInteger(pageSize) && pageSize > 0
      ? pageSize
      : PAGE_SIZE

  const pages = []
  for (let i = 0; i < rows.length; i += size) {
    pages.push(rows.slice(i, i + size))
  }
  return pages
}

export default {
  PLACEHOLDER,
  STATE_LIVE,
  STATE_HIDDEN,
  STATE_PROPOSAL,
  ALL,
  PAGE_SIZE,
  SORTABLE_COLUMNS,
  deriveState,
  deriveRowViewModel,
  deriveRows,
  filterRows,
  compareByColumn,
  sortRows,
  paginate
}
